import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthContext } from '../../../core/providers/AuthProvider'
import { userService, socketClient } from '../../../core/services'
import { toast } from 'react-toastify'
import c from './assets/sass/energy.module.scss'

export const Energy = () => {
    const { t } = useTranslation()
    const { user, updateUserData, isAuthenticated } = useAuthContext()

    const [energy, setEnergy] = useState(user?.energy ?? 0)
    const [maxEnergy, setMaxEnergy] = useState(user?.maxEnergy ?? 100)

    const [hasInitialized, setHasInitialized] = useState(false)

    useEffect(() => {
        if (isAuthenticated && user && !hasInitialized) {
            setEnergy(user.energy ?? 0)
            setMaxEnergy(user.maxEnergy ?? 100)
            setHasInitialized(true)
        }
    }, [isAuthenticated, user, hasInitialized])

    useEffect(() => {
        if (isAuthenticated && user) {
            setEnergy(user.energy ?? 0)
            setMaxEnergy(user.maxEnergy ?? 100)
        }
    }, [user, isAuthenticated])

    // УБРАЛИ ПАССИВНОЕ ВОССТАНОВЛЕНИЕ С КЛИЕНТА!
    // Теперь сервер полностью контролирует энергию
    // Восстановление происходит на бэкенде при каждом запросе getUserById

    // Периодически запрашиваем обновленные данные с сервера
    useEffect(() => {
        if (!isAuthenticated) return

        const syncInterval = setInterval(async () => {
            try {
                // Получаем актуальные данные с сервера (включая восстановленную энергию)
                const response = await userService.getBalance()
                if (response.success && response.user) {
                    updateUserData({
                        energy: response.user.energy,
                        maxEnergy: response.user.maxEnergy,
                        balance: response.user.balance,
                    })
                }
            } catch (error) {
                console.debug('Energy sync error:', error)
            }
        }, 60000) // Каждую минуту синхронизируем с сервером

        return () => clearInterval(syncInterval)
    }, [isAuthenticated, updateUserData])

    useEffect(() => {
        if (!isAuthenticated) return

        const handleEnergyUpdate = async (data) => {
            console.log('🔋 Energy updated from server:', data)
            if (data.energy !== undefined) {
                setEnergy(data.energy)
                if (data.maxEnergy !== undefined) {
                    setMaxEnergy(data.maxEnergy)
                }
                const updates = {
                    energy: data.energy,
                    maxEnergy: data.maxEnergy,
                }
                if (data.balance !== undefined) {
                    displayBalanceRef.current = Number(data.balance)
                    updates.balance = data.balance
                }
                await updateUserData(updates)
            }
        }

        const handleBalanceUpdate = async (data) => {
            console.log('💰 Balance updated from server:', data)
            if (data.balance !== undefined) {
                displayBalanceRef.current = Number(data.balance)
                await updateUserData({
                    balance: data.balance,
                })
            }
        }

        socketClient.on('energy_updated', handleEnergyUpdate)
        socketClient.on('balance_updated', handleBalanceUpdate)

        return () => {
            socketClient.off('energy_updated', handleEnergyUpdate)
            socketClient.off('balance_updated', handleBalanceUpdate)
        }
    }, [isAuthenticated, updateUserData])

    const tapBufferRef = useRef({ taps: 0, timer: null, requestId: 0 })
    const displayBalanceRef = useRef(0)

    useEffect(() => {
        if (user?.balance) {
            displayBalanceRef.current = Number(user.balance)
        }
    }, [user?.balance])

    const flushBuffer = useCallback(() => {
        const { taps } = tapBufferRef.current
        tapBufferRef.current.taps = 0
        if (taps > 0 && isAuthenticated) {
            const requestId = `${Date.now()}_${tapBufferRef.current.requestId++}`
            const send = async () => {
                try {
                    // Больше НЕ передаём энергию - сервер сам контролирует
                    const resp = await userService.updateEnergy(taps, requestId)
                    if (resp && resp.accepted === false) {
                        setTimeout(
                            () =>
                                userService
                                    .updateEnergy(taps, requestId)
                                    .catch(() => {}),
                            100
                        )
                    }
                } catch {}
            }
            send()
        }
    }, [isAuthenticated])

    const handleTap = useCallback(() => {
        if (!user) return

        if (energy <= 0) {
            toast(t('main.energy.dailyLimit'), { type: 'error' })
            return
        }

        const newEnergy = Math.max(energy - 1, 0)
        setEnergy(newEnergy)

        displayBalanceRef.current += 1
        try {
            updateUserData({
                energy: newEnergy,
                balance: String(displayBalanceRef.current),
            })
        } catch {}

        tapBufferRef.current.taps += 1
        if (!tapBufferRef.current.timer) {
            const bufMs = Number(process.env.REACT_APP_TAP_BUFFER_MS || 100)
            tapBufferRef.current.timer = setTimeout(() => {
                tapBufferRef.current.timer = null
                flushBuffer()
            }, bufMs)
        }
    }, [user, energy, flushBuffer, updateUserData])

    useEffect(() => {
        if (isAuthenticated) {
            window.energyTap = handleTap
        }
        return () => {
            window.energyTap = null
        }
    }, [handleTap, isAuthenticated])

    useEffect(() => {
        const bufferRef = tapBufferRef
        return () => {
            if (bufferRef.current && bufferRef.current.timer) {
                clearTimeout(bufferRef.current.timer)
            }
        }
    }, [])

    const progress = Math.round((energy / maxEnergy) * 100)

    return (
        <div className={c.component}>
            <div className={c.row}>
                <span>{energy}</span> / {maxEnergy}
            </div>
            <div className={c.progress}>
                <div className={c.line} style={{ width: `${progress}%` }} />
            </div>
        </div>
    )
}
